"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.editWardReturnReasonSchema = exports.recentWardReturnsQuerySchema = exports.returnMovementLineSchema = exports.receiveInventoryMovementSchema = exports.movementNumberQuerySchema = exports.rejectInventoryMovementSchema = exports.updateInventoryMovementSchema = exports.createInventoryMovementSchema = exports.inventoryMovementLineInputSchema = void 0;
const zod_1 = require("zod");
/**
 * A line supports either an explicit `batchId` or a `drugId`/`itemId` +
 * server-FEFO-resolved-at-creation path (generalizes `StockTransfer`'s
 * existing `createTransfer`/`createTransferByDrug` split to per-line).
 * Exactly one of `drugId`/`itemId` and exactly one of `batchId`/`drugId`
 * or `batchId`/`itemId` must be present.
 */
exports.inventoryMovementLineInputSchema = zod_1.z
    .object({
    drugId: zod_1.z.string().uuid().optional(),
    itemId: zod_1.z.string().uuid().optional(),
    batchId: zod_1.z.string().uuid().optional(),
    quantity: zod_1.z.number().positive(),
    uomId: zod_1.z.string().uuid().optional(),
})
    .refine((line) => Boolean(line.drugId) !== Boolean(line.itemId), {
    message: 'Exactly one of drugId or itemId must be provided',
})
    .refine((line) => Boolean(line.batchId) || Boolean(line.drugId) || Boolean(line.itemId), {
    message: 'Either batchId (explicit) or drugId/itemId (FEFO-resolved) must be provided',
});
exports.createInventoryMovementSchema = zod_1.z.object({
    movementTypeId: zod_1.z.string().uuid(),
    branchId: zod_1.z.string().uuid(),
    sourceLocationId: zod_1.z.string().uuid(),
    destinationLocationId: zod_1.z.string().uuid().optional(),
    patientId: zod_1.z.string().uuid().optional(),
    departmentId: zod_1.z.string().uuid().optional(),
    referenceModule: zod_1.z.string().max(50).optional(),
    referenceDocument: zod_1.z.string().max(100).optional(),
    remarks: zod_1.z.string().optional(),
    lines: zod_1.z.array(exports.inventoryMovementLineInputSchema).min(1),
});
// Editing is DRAFT-only (enforced service-side) and always re-saves the
// full header+lines — same shape as create, since an edit is really
// "replace this draft's content," not a partial patch. Mirrors
// `updateGoodsReceiptNoteSchema`'s own identical precedent.
exports.updateInventoryMovementSchema = exports.createInventoryMovementSchema;
exports.rejectInventoryMovementSchema = zod_1.z.object({ reason: zod_1.z.string().min(1) });
// Return workflow overhaul — Ward Return's "Original Document" / scan
// step, GET /inventory-movements/lookup?movementNumber=. Exact match,
// same posture as Sales Return's own `saleNumberQuerySchema`.
exports.movementNumberQuerySchema = zod_1.z.object({
    movementNumber: zod_1.z.string().trim().min(1),
});
// POST /inventory-movements/:id/receive body. Owner, 2026-10-30 (rack system): the receiver picks a RACK PER LINE from the store's rack master —
// `rackId` is the default for every line, `lineRacks` overrides it line by line (a null rackId = leave that line unracked). The old free-text
// `rackLocation` is gone: an older browser tab that still sends it is simply ignored (unknown keys are stripped), and the batch arrives unracked.
exports.receiveInventoryMovementSchema = zod_1.z.object({
    rackId: zod_1.z.string().uuid().nullable().optional(),
    lineRacks: zod_1.z
        .array(zod_1.z.object({ lineId: zod_1.z.string().uuid(), rackId: zod_1.z.string().uuid().nullable() }))
        .max(500)
        .optional(),
});
// POST /inventory-movements/lines/:lineId/return body. `movementId` is
// included (not derived server-side from the line alone) so the
// handler can cheaply validate the line actually belongs to the
// document the caller looked up, mirroring Sales Return's own
// `sale`+`line` pairing. `quantity` omitted = return everything still
// returnable on this line (see `WardReturnService.returnMovementLine()`).
exports.returnMovementLineSchema = zod_1.z.object({
    movementId: zod_1.z.string().uuid(),
    reason: zod_1.z.string().trim().min(1).max(255),
    quantity: zod_1.z.number().positive().optional(),
    destinationLocationId: zod_1.z.string().uuid().optional(),
    /** Ignored since the rack system (2026-10-30): racks are changed with Re-rack, never by typing text on a return. Still accepted so an open older tab does not fail. */
    destinationRackLocation: zod_1.z.string().trim().min(1).max(50).optional(),
});
// Real feedback — Ward Return's own Return History tab,
// GET /inventory-movements/returns/recent. Mirrors Sales Return's own
// `recentReturnsQuerySchema` (`saleNumber` → `movementNumber`).
exports.recentWardReturnsQuerySchema = zod_1.z.object({
    limit: zod_1.z.coerce.number().int().positive().max(200).optional(),
    returnNumber: zod_1.z.string().trim().min(1).optional(),
    movementNumber: zod_1.z.string().trim().min(1).optional(),
    fromDate: zod_1.z.coerce.date().optional(),
    toDate: zod_1.z.coerce.date().optional(),
});
// Real feedback — Ward Return History's "Edit Reason" action. Same
// narrow, audited exception as Sales Return's own `editReturnReasonSchema`
// — only the reason text is ever editable on a posted StockReturnEntry.
exports.editWardReturnReasonSchema = zod_1.z.object({
    reason: zod_1.z.string().trim().min(1).max(255),
});
//# sourceMappingURL=inventory-movement.schema.js.map